import {
  Document,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  formatPdfCellValue,
  formatPdfValue,
} from "./prismReportTextUtils";

export { formatPdfCellValue } from "./prismReportTextUtils";

const styles = StyleSheet.create({
  page: {
    paddingTop: 14,
    paddingRight: 16,
    paddingBottom: 18,
    paddingLeft: 16,
    fontSize: 7.4,
    fontFamily: "Helvetica",
    color: "#1f1f1f",
  },
  reportHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 6,
    paddingBottom: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: "#d9d9d9",
  },
  headerMain: {
    flexGrow: 1,
    paddingRight: 12,
  },
  title: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 2,
    color: "#8c1d31",
  },
  meta: {
    color: "#595959",
    fontSize: 6.7,
    textAlign: "right",
  },
  metaBlock: {
    width: 155,
    alignItems: "flex-end",
  },
  summary: {
    fontSize: 8.3,
    fontWeight: "bold",
    color: "#00573f",
  },
  filters: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 6,
  },
  filterItem: {
    paddingTop: 2,
    paddingRight: 4,
    paddingBottom: 2,
    paddingLeft: 4,
    marginRight: 3,
    marginBottom: 3,
    borderWidth: 0.5,
    borderColor: "#d9d9d9",
    backgroundColor: "#fafafa",
  },
  filterLabel: {
    color: "#595959",
  },
  table: {
    borderTopWidth: 0.5,
    borderLeftWidth: 0.5,
    borderColor: "#cfcfcf",
  },
  row: {
    flexDirection: "row",
  },
  headerCell: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    color: "#262626",
  },
  cell: {
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: "#d9d9d9",
    fontSize: 6.35,
    paddingTop: 2.2,
    paddingRight: 2.4,
    paddingBottom: 2.2,
    paddingLeft: 2.4,
    lineHeight: 1.08,
    minHeight: 11,
    flexShrink: 0,
  },
  cellText: {
    lineHeight: 1.08,
  },
  empty: {
    padding: 8,
    borderWidth: 0.5,
    borderColor: "#d9d9d9",
    backgroundColor: "#fafafa",
  },
  footer: {
    position: "absolute",
    right: 16,
    bottom: 7,
    fontSize: 6.5,
    color: "#8c8c8c",
  },
  chart: {
    marginBottom: 8,
    paddingTop: 4,
  },
  chartRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2.5,
  },
  chartLabel: {
    width: 30,
  },
  chartTrack: {
    flexGrow: 1,
    height: 6,
    backgroundColor: "#f0f0f0",
  },
  chartBar: {
    height: 6,
    backgroundColor: "#00573F",
  },
  chartAmount: {
    width: 68,
    textAlign: "right",
  },
});

export const downloadCsv = (filename, columns, rows) => {
  const escapeCsvValue = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csvRows = [
    columns.map((column) => escapeCsvValue(column.header)).join(","),
    ...rows.map((row) =>
      columns
        .map((column) => escapeCsvValue(column.accessor(row)))
        .join(","),
    ),
  ];

  const blob = new Blob([`\uFEFF${csvRows.join("\r\n")}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getColumnWidth = (column, fallbackWidth) => {
  const configuredWidth = column.pdfWidth ?? column.width;

  if (configuredWidth === undefined || configuredWidth === null) {
    return fallbackWidth;
  }

  return typeof configuredWidth === "number"
    ? `${configuredWidth}%`
    : configuredWidth;
};

const getPdfHeader = (column) => column.pdfHeader || column.header;

const getCellStyle = (column, width) => [
  styles.cell,
  { width },
  ...(column.pdfAlign ? [{ textAlign: column.pdfAlign }] : []),
];

const ReportTable = ({ columns, rows }) => {
  if (!rows.length) {
    return <Text style={styles.empty}>No rows found for the selected filters.</Text>;
  }

  const fallbackWidth = `${100 / columns.length}%`;

  return (
    <View style={styles.table}>
      <View style={styles.row} fixed>
        {columns.map((column) => {
          const width = getColumnWidth(column, fallbackWidth);

          return (
            <Text
              key={column.header}
              style={[...getCellStyle(column, width), styles.headerCell]}
            >
              {formatPdfCellValue(getPdfHeader(column), column)}
            </Text>
          );
        })}
      </View>
      {rows.map((row, rowIndex) => (
        <View key={row.key || row.id || rowIndex} style={styles.row}>
          {columns.map((column) => {
            const width = getColumnWidth(column, fallbackWidth);

            return (
              <Text key={column.header} style={getCellStyle(column, width)}>
                <Text style={styles.cellText}>
                  {formatPdfCellValue(column.accessor(row), column)}
                </Text>
              </Text>
            );
          })}
        </View>
      ))}
    </View>
  );
};

const MonthlyChart = ({ data }) => {
  const maxTotal = Math.max(...data.map((item) => Number(item.total || 0)), 0);

  return (
    <View style={styles.chart}>
      {data.map((item) => {
        const total = Number(item.total || 0);
        const width = maxTotal > 0 ? `${Math.max((total / maxTotal) * 100, 1)}%` : "0%";

        return (
          <View key={item.month} style={styles.chartRow}>
            <Text style={styles.chartLabel}>{item.shortMonth || item.month}</Text>
            <View style={styles.chartTrack}>
              <View style={[styles.chartBar, { width }]} />
            </View>
            <Text style={styles.chartAmount}>{formatPdfValue(item.formattedTotal)}</Text>
          </View>
        );
      })}
    </View>
  );
};

const PrismReportPdfDocument = ({
  title,
  filters = [],
  summary = null,
  columns,
  rows,
  monthlyChartData = null,
}) => (
  <Document>
    <Page size="A4" orientation="landscape" style={styles.page}>
      <View style={styles.reportHeader}>
        <View style={styles.headerMain}>
          <Text style={styles.title}>{title}</Text>
          {summary && <Text style={styles.summary}>{summary}</Text>}
        </View>
        <View style={styles.metaBlock}>
          <Text style={styles.meta}>Generated {new Date().toLocaleString()}</Text>
        </View>
      </View>
      {filters.length > 0 && (
        <View style={styles.filters}>
          {filters.map((filter) => (
            <Text key={filter.label} style={styles.filterItem}>
              <Text style={styles.filterLabel}>{filter.label}: </Text>
              {formatPdfCellValue(filter.value || "All", { pdfWrapLength: 36 })}
            </Text>
          ))}
        </View>
      )}
      {monthlyChartData && <MonthlyChart data={monthlyChartData} />}
      <ReportTable columns={columns} rows={rows} />
      <Text
        fixed
        style={styles.footer}
        render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        }
      />
    </Page>
  </Document>
);

export default PrismReportPdfDocument;
